import jwt from "jsonwebtoken";
import { replace } from "connected-react-router";
import * as types from "./actionTypes";
import API from "../../services/UserService";
import {JWT_PUBLIC_KEY, ROLES} from "../../components/common/Const";
import {apiCallError, beginApiCall} from "./apiStatusActions";
import { decodeStoredToken, getStoredAuthToken, removeAuthToken } from "../../services/authTokenStorage";
import { recoverAuthentication, refreshAccessToken, revokeStoredSession, takeReturnDestination } from "../../services/sessionCoordinator";

const SUPPORTED_AUTH_ROLES = ["ADMIN", "OPERATOR", "TEACHER", "CLIENT"];
const SUPPORTED_PLATFORM_ROLES = ["SUPER_ADMIN"];

function normalizeRole(value) {
  if (!value) return null;
  const normalized = String(value).toUpperCase();
  return SUPPORTED_AUTH_ROLES.includes(normalized) ? normalized : null;
}

function normalizeRoles(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.map(normalizeRole).filter(Boolean))];
}

function normalizePlatformRoles(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values
    .map(value => String(value || "").trim().toUpperCase())
    .filter(value => SUPPORTED_PLATFORM_ROLES.includes(value)))];
}

function normalizeAuthUser(decoded = {}) {
  const role = normalizeRole(decoded.role) || (
    Array.isArray(decoded.roles) && decoded.roles.length === 1
      ? normalizeRole(decoded.roles[0])
      : null
  );

  return {
    id: decoded.id,
    email: decoded.email || decoded.id,
    role,
    teacherId: decoded.teacherId || null,
    clientId: decoded.clientId || null,
    schoolId: decoded.schoolId || null,
    schoolName: decoded.schoolName || null,
    activeContextKey: decoded.activeContextKey || null,
    ...(decoded.sid ? { sessionId: decoded.sid, sessionVersion: decoded.sv } : {}),
    canSwitchContext: decoded.canSwitchContext || false,
    ...(normalizeRoles(decoded.systemRoles).length > 0 ? { systemRoles: normalizeRoles(decoded.systemRoles) } : {}),
    ...(normalizePlatformRoles(decoded.platformRoles).length > 0 ? { platformRoles: normalizePlatformRoles(decoded.platformRoles) } : {}),
  };
}

export function signInSuccess(user) {
  return { type: types.SIGN_IN_SUCCESS, user };
}

function completeSignIn(dispatch, user, getState) {
  const state = getState ? getState() : {};
  const location = state.router && state.router.location;
  const pathname = location && location.pathname;
  const portal = user.role === ROLES.CLIENT ? "/client" : "/manage";
  const supportDestination = pathname && (
    /^\/support\/[^/]+\/[^/]+$/.test(pathname) ||
    /^\/(client|manage)\/support(?:\/|$)/.test(pathname)
  );
  const destination = supportDestination
    ? pathname.replace(/^\/(client|manage)\/support/, `${portal}/support`) + (location.search || "")
    : takeReturnDestination(portal);
  // Navigate before publishing the new session, which can unmount the login form.
  dispatch(replace(destination));
  const decoded = decodeStoredToken();
  dispatch(signInSuccess(decoded && decoded.sid ? { ...user, sessionId: decoded.sid, sessionVersion: decoded.sv } : user));
  return user;
}

export function authFlowStepSuccess() {
  return { type: types.AUTH_FLOW_STEP_SUCCESS };
}

export function getLoginContextsSuccess() {
  return { type: types.GET_LOGIN_CONTEXTS_SUCCESS };
}

export function signIn(userCredentials) {
  // eslint-disable-next-line no-unused-vars
  return (dispatch, getState) => {
    dispatch(beginApiCall('signIn'));
    return API.signIn(userCredentials)
      .then((userLoggedIn) => {
        if (userLoggedIn && userLoggedIn.status) {
          dispatch(authFlowStepSuccess());
          return userLoggedIn;
        }
        return completeSignIn(dispatch, userLoggedIn, getState);
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function signInWithGoogle(googleToken) {
  return (dispatch, getState) => {
    dispatch(beginApiCall("signInWithGoogle"));
    return API.signInWithGoogle(googleToken)
      .then((userLoggedIn) => {
        if (userLoggedIn && userLoggedIn.status) {
          dispatch(authFlowStepSuccess());
          return userLoggedIn;
        }
        return completeSignIn(dispatch, userLoggedIn, getState);
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

function apiOnlyAction(apiName, apiCall) {
  return (dispatch) => {
    dispatch(beginApiCall(apiName));
    return apiCall()
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function signUpWithPassword(payload) {
  return apiOnlyAction("signUpWithPassword", () => API.signUpWithPassword(payload));
}

export function confirmEmailVerification(token) {
  return apiOnlyAction("confirmEmailVerification", () => API.confirmEmailVerification(token));
}

export function resendEmailVerification(email) {
  return apiOnlyAction("resendEmailVerification", () => API.resendEmailVerification(email));
}

export function selectLoginContext(contextChoiceToken, contextKey) {
  return (dispatch, getState) => {
    dispatch(beginApiCall("selectLoginContext"));
    return API.selectLoginContext(contextChoiceToken, contextKey)
      .then(userLoggedIn => completeSignIn(dispatch, userLoggedIn, getState))
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function selectSuperAdminSchool(schoolChoiceToken, schoolId) {
  return (dispatch, getState) => {
    dispatch(beginApiCall("selectSuperAdminSchool"));
    return API.selectSuperAdminSchool(schoolChoiceToken, schoolId)
      .then(userLoggedIn => completeSignIn(dispatch, userLoggedIn, getState))
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function getLoginContexts() {
  return (dispatch) => {
    dispatch(beginApiCall("getLoginContexts"));
    return API.getLoginContexts()
      .then((contextChoice) => {
        dispatch(getLoginContextsSuccess());
        return contextChoice;
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function completeSsoProfile(profileCompletionToken, details) {
  return (dispatch, getState) => {
    dispatch(beginApiCall("completeSsoProfile"));
    return API.completeSsoProfile(profileCompletionToken, details)
      .then(userLoggedIn => completeSignIn(dispatch, userLoggedIn, getState))
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function signOut() {
  revokeStoredSession().catch(() => undefined);
  removeAuthToken();
  return { type: types.SIGN_OUT };
}

export function checkToken() {
  return (dispatch) => {
    const token = getStoredAuthToken();
    const decodedToken = decodeStoredToken(token);
    if (token && decodedToken && decodedToken.exp * 1000 <= Date.now()) {
      return refreshAccessToken(token).then(() => dispatch(checkToken())).catch(() => undefined);
    }
    if (token) {
      const secret = JWT_PUBLIC_KEY;
      jwt.verify(token, secret, {algorithms: ['RS256']}, (err, decoded) => {
        if (err) {
          // eslint-disable-next-line no-console
          console.log('token error', err);
          recoverAuthentication(err.name === "TokenExpiredError" ? "expired" : "required");
          dispatch({ type: types.SIGN_OUT });
        }

        if (decoded) {
          // console.log('token found and verified', decoded);
          dispatch(signInSuccess(normalizeAuthUser(decoded)));
        }
      });
    }
    return undefined;
  };
}
